import { FormattedMessage } from "react-intl";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@ps-design-system/components/Carousel";
import { Text } from "@ps-design-system/components/Text";

import useDateBasedPricingBanner from "../hooks/useDateBasedPricingBanner";
import useFSVHostGuideBanner from "../hooks/useFSVHostGuideBanner";
import useHostHubPromotedByHost from "../hooks/useHostHubPromotedByHost";
import useVendorRecommendationBanner from "../hooks/useVendorRecommendationBanner";

import DateBasedPricingBanner from "./DateBasedPricingBanner";
import FSVHostGuideBanner from "./FSVHostGuideBanner";
import HostHubPromotedByHostBanner from "./HostHubPromotedByHostBanner";
import VendorRecommendationBanner from "./VendorRecommendationBanner";

// Slides stretch to the tallest one; make each banner fill its slide so every
// card in the carousel renders at the same height.
const CAROUSEL_ITEM_CLASS_NAME = "[&>*]:tw-h-full";

const HostHubBannerCarousel = () => {
  const {
    dismiss: dismissDateBasedPricing,
    isVisible: isDateBasedPricingVisible,
  } = useDateBasedPricingBanner();
  const {
    callout: fsvHostGuideCallout,
    dismiss: dismissFsvHostGuide,
    isVisible: isFsvHostGuideVisible,
  } = useFSVHostGuideBanner();
  const {
    isVisible: isVendorRecommendationVisible,
    dismiss: dismissVendorRecommendation,
  } = useVendorRecommendationBanner();
  const { isVisible: isPromotedByHostVisible, dismiss: dismissPromotedByHost } =
    useHostHubPromotedByHost();
  const totalItems =
    Number(isDateBasedPricingVisible) +
    Number(isFsvHostGuideVisible) +
    Number(isVendorRecommendationVisible) +
    Number(isPromotedByHostVisible);

  if (totalItems === 0) return null;

  if (totalItems === 1) {
    return (
      <div className="md:tw-mt-16">
        {isDateBasedPricingVisible && (
          <DateBasedPricingBanner dismiss={dismissDateBasedPricing} />
        )}
        {fsvHostGuideCallout && (
          <FSVHostGuideBanner
            callout={fsvHostGuideCallout}
            dismiss={dismissFsvHostGuide}
          />
        )}
        {isVendorRecommendationVisible && (
          <VendorRecommendationBanner dismiss={dismissVendorRecommendation} />
        )}
        {isPromotedByHostVisible && (
          <HostHubPromotedByHostBanner dismiss={dismissPromotedByHost} />
        )}
      </div>
    );
  }

  return (
    <div className="md:tw-mt-16">
      <div className="tw-flex tw-items-center tw-w-full tw-justify-between tw-mb-20">
        <Text typeface="headline-3-m">
          <FormattedMessage
            id="host_hub.carousel.header"
            defaultMessage="Take a look"
          />
        </Text>
      </div>
      <Carousel>
        <div className="tw-hidden md:tw-flex tw-items-center tw-justify-end tw-gap-24 tw-mb-8">
          <CarouselPrevious className="tw-static tw-translate-y-0" />
          <CarouselNext className="tw-static tw-translate-y-0" />
        </div>
        <CarouselContent>
          {isDateBasedPricingVisible && (
            <CarouselItem className={CAROUSEL_ITEM_CLASS_NAME}>
              <DateBasedPricingBanner dismiss={dismissDateBasedPricing} />
            </CarouselItem>
          )}
          {fsvHostGuideCallout && (
            <CarouselItem className={CAROUSEL_ITEM_CLASS_NAME}>
              <FSVHostGuideBanner
                callout={fsvHostGuideCallout}
                dismiss={dismissFsvHostGuide}
              />
            </CarouselItem>
          )}
          {isVendorRecommendationVisible && (
            <CarouselItem className={CAROUSEL_ITEM_CLASS_NAME}>
              <VendorRecommendationBanner
                dismiss={dismissVendorRecommendation}
              />
            </CarouselItem>
          )}
          {isPromotedByHostVisible && (
            <CarouselItem className={CAROUSEL_ITEM_CLASS_NAME}>
              <HostHubPromotedByHostBanner dismiss={dismissPromotedByHost} />
            </CarouselItem>
          )}
        </CarouselContent>
        <div className="tw-flex tw-items-center tw-justify-between tw-w-full md:tw-hidden">
          <CarouselPrevious className="tw-static tw-translate-y-0" />
          <CarouselNext className="tw-static tw-translate-y-0" />
        </div>
      </Carousel>
    </div>
  );
};

export default HostHubBannerCarousel;
